import React, { useState, useEffect, useRef, useCallback } from "react";
import Modal from "react-modal";
import Swal from "sweetalert2";
import axiosInstance from "../../config/axiosConfig";
import { CircularProgress } from "@mui/material";
import CollectionsIcon from "@mui/icons-material/Collections";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";

const customModalStyles = {
  content: {
    padding: "0",
    width: "92%",
    maxWidth: "840px",
    margin: "auto",
    borderRadius: "16px",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    maxHeight: "90vh",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    border: "none",
  },
  overlay: {
    backgroundColor: "rgba(15, 23, 42, 0.65)",
    backdropFilter: "blur(4px)",
    zIndex: 9999,
  },
};

const MAX_IMAGES = 20;

export default function FacilityGalleryModal({
  facility,
  isOpen,
  onClose,
  onGalleryUpdated,
}) {
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const facilityKey = facility?.slug || facility?.id || facility?._id;

  // Load facility gallery
  const loadGallery = useCallback(async () => {
    if (!facilityKey) return;
    setLoading(true);
    try {
      const res = await axiosInstance.get(`campus-facilities/${facilityKey}/gallery`);
      const list = res?.data?.data || [];
      setGallery(list);
    } catch (err) {
      console.error("Failed to load facility gallery:", err);
      // If endpoint not reached or 404, fallback to facility.gallery if provided
      if (Array.isArray(facility?.gallery)) {
        setGallery(facility.gallery);
      } else {
        setGallery([]);
      }
    } finally {
      setLoading(false);
    }
  }, [facilityKey, facility]);

  useEffect(() => {
    if (isOpen && facilityKey) {
      loadGallery();
      setSelectedFiles([]);
      setPreviews([]);
      setUploadProgress(0);
      setUploadStatus("");
    }
  }, [isOpen, facilityKey, loadGallery]);

  // Clean up object URLs when previews change
  useEffect(() => {
    return () => {
      previews.forEach((p) => URL.revokeObjectURL(p.url));
    };
  }, [previews]);

  const handleFilesChosen = (files) => {
    if (!files || files.length === 0) return;

    const remainingSlots = Math.max(0, MAX_IMAGES - (gallery.length + selectedFiles.length));
    if (remainingSlots <= 0) {
      Swal.fire({
        icon: "warning",
        title: "Limit Reached",
        text: `Each facility can have a maximum of ${MAX_IMAGES} photos. Please delete some existing photos before adding more.`,
      });
      return;
    }

    const validFiles = [];
    const validPreviews = [];
    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif", "image/svg+xml"];

    let skipped = 0;
    Array.from(files).forEach((file) => {
      if (!allowedTypes.includes(file.type)) {
        skipped++;
        return;
      }
      if (validFiles.length < remainingSlots) {
        validFiles.push(file);
        validPreviews.push({
          file,
          url: URL.createObjectURL(file),
          name: file.name,
          size: (file.size / (1024 * 1024)).toFixed(2),
        });
      }
    });

    if (skipped > 0) {
      Swal.fire("Note", `${skipped} file(s) skipped due to unsupported file format. Allowed: JPG, PNG, WEBP, GIF.`, "info");
    }

    setSelectedFiles((prev) => [...prev, ...validFiles]);
    setPreviews((prev) => [...prev, ...validPreviews]);
  };

  const removeSelectedFile = (idx) => {
    const target = previews[idx];
    if (target?.url) URL.revokeObjectURL(target.url);
    setSelectedFiles((prev) => prev.filter((_, i) => i !== idx));
    setPreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;
    setUploading(true);
    setUploadProgress(0);
    setUploadStatus(`Uploading ${selectedFiles.length} photo(s)... 0%`);

    try {
      const formData = new FormData();
      selectedFiles.forEach((file) => {
        formData.append("images[]", file);
      });

      const res = await axiosInstance.post(
        `campus-facilities/${facilityKey}/gallery`,
        formData,
        {
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress(percent);
              if (percent < 100) {
                setUploadStatus(`Uploading ${selectedFiles.length} photo(s)... ${percent}%`);
              } else {
                setUploadStatus("Processing and optimizing photos on server...");
              }
            }
          },
        }
      );

      const updated = res?.data?.data || [];
      setGallery(updated);
      setSelectedFiles([]);
      setPreviews([]);
      setUploadProgress(100);
      setUploadStatus("Upload complete!");

      if (onGalleryUpdated) {
        onGalleryUpdated(updated);
      }

      Swal.fire({
        icon: "success",
        title: "Photos Uploaded!",
        text: `${selectedFiles.length} photo(s) added successfully to ${facility.title}. They are now live on the website.`,
        timer: 2200,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error("Gallery upload error:", err);
      let msg = err.response?.data?.message || err.message || "Failed to upload photos.";
      if (err.response?.status === 413) {
        msg = "The selected files exceed the server upload limit. Please try uploading fewer photos or smaller file sizes at a time.";
      }
      Swal.fire("Upload Failed", msg, "error");
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadStatus("");
    }
  };

  const handleDeletePhoto = async (photo) => {
    const photoId = photo.id || photo._id || photo.fileName;
    const result = await Swal.fire({
      title: "Remove this photo?",
      text: `Are you sure you want to delete "${photo.originalName || photo.fileName || "this photo"}"? It will no longer show on the website.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, delete",
    });

    if (!result.isConfirmed) return;

    try {
      const res = await axiosInstance.delete(
        `campus-facilities/${facilityKey}/gallery/${photoId}`
      );
      const updated = res?.data?.data || gallery.filter((p) => (p.id || p.fileName) !== photoId);
      setGallery(updated);

      if (onGalleryUpdated) {
        onGalleryUpdated(updated);
      }

      Swal.fire({
        icon: "success",
        title: "Photo Deleted",
        text: "The photo has been removed from the facility gallery.",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error("Delete photo error:", err);
      const msg = err.response?.data?.message || err.message || "Failed to delete photo.";
      Swal.fire("Error", msg, "error");
    }
  };

  if (!isOpen || !facility) return null;

  const currentCount = gallery.length;
  const remainingSlots = Math.max(0, MAX_IMAGES - currentCount);
  const percentageUsed = Math.min(100, Math.round((currentCount / MAX_IMAGES) * 100));

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={() => !uploading && onClose()}
      style={customModalStyles}
      contentLabel="Manage Facility Gallery"
    >
      {/* Modal Header */}
      <div
        style={{
          padding: "18px 24px",
          borderBottom: "1px solid #e2e8f0",
          backgroundColor: "#f8fafc",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              backgroundColor: "#dbeafe",
              color: "#2563eb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CollectionsIcon />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#1e293b" }}>
              Facility Gallery: {facility.title}
            </h3>
            <p style={{ margin: "2px 0 0", fontSize: 12, color: "#64748b" }}>
              Photos uploaded here will automatically display on the public website at{" "}
              <code style={{ background: "#e2e8f0", padding: "1px 6px", borderRadius: 4 }}>
                /facilities/{facility.slug}
              </code>
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => !uploading && onClose()}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "#64748b",
            padding: 4,
            borderRadius: 6,
          }}
        >
          <CloseIcon />
        </button>
      </div>

      {/* Modal Body */}
      <div style={{ padding: "20px 24px", overflowY: "auto", flex: 1 }}>
        {/* Quota & Progress Bar */}
        <div
          style={{
            backgroundColor: "#f1f5f9",
            borderRadius: 12,
            padding: "14px 18px",
            marginBottom: 20,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>
              Gallery Capacity: {currentCount} of {MAX_IMAGES} Photos
            </span>
            <span
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: remainingSlots === 0 ? "#ef4444" : "#2563eb",
                backgroundColor: remainingSlots === 0 ? "#fee2e2" : "#dbeafe",
                padding: "2px 8px",
                borderRadius: 12,
              }}
            >
              {remainingSlots} slots available
            </span>
          </div>
          <div
            style={{
              width: "100%",
              height: 6,
              backgroundColor: "#cbd5e1",
              borderRadius: 3,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${percentageUsed}%`,
                height: "100%",
                backgroundColor: percentageUsed >= 90 ? "#ef4444" : "#2563eb",
                transition: "width 0.3s ease",
              }}
            />
          </div>
        </div>

        {/* Upload Dropzone */}
        {remainingSlots > 0 ? (
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragging(false);
              if (e.dataTransfer.files) handleFilesChosen(e.dataTransfer.files);
            }}
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: `2px dashed ${isDragging ? "#2563eb" : "#94a3b8"}`,
              backgroundColor: isDragging ? "#eff6ff" : "#f8fafc",
              borderRadius: 12,
              padding: "24px 16px",
              textAlign: "center",
              cursor: "pointer",
              marginBottom: 20,
              transition: "all 0.2s ease",
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
              onChange={(e) => {
                if (e.target.files) handleFilesChosen(e.target.files);
                e.target.value = "";
              }}
              style={{ display: "none" }}
            />
            <CloudUploadIcon style={{ fontSize: 40, color: "#3b82f6", marginBottom: 6 }} />
            <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: "#1e293b" }}>
              Click to select photos or drag & drop images here
            </p>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: "#64748b" }}>
              Upload multiple images at once (up to {remainingSlots} photos &bull; JPG, PNG, WEBP, GIF)
            </p>
          </div>
        ) : (
          <div
            style={{
              backgroundColor: "#fee2e2",
              border: "1px solid #fca5a5",
              borderRadius: 12,
              padding: "12px 16px",
              marginBottom: 20,
              fontSize: 13,
              color: "#991b1b",
              textAlign: "center",
              fontWeight: 600,
            }}
          >
            Maximum limit of 20 photos reached for this facility. Delete existing photos to add new ones.
          </div>
        )}

        {/* Selected Files Ready to Upload */}
        {previews.length > 0 && (
          <div
            style={{
              marginBottom: 24,
              padding: 16,
              backgroundColor: "#f0fdf4",
              borderRadius: 12,
              border: "1px solid #bbf7d0",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 12,
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 700, color: "#166534" }}>
                Ready to Upload ({previews.length} photo{previews.length > 1 ? "s" : ""}):
              </span>
              <button
                type="button"
                onClick={handleUpload}
                disabled={uploading}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 18px",
                  backgroundColor: uploading ? "#94a3b8" : "#16a34a",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  cursor: uploading ? "wait" : "pointer",
                  fontWeight: 700,
                  fontSize: 13,
                  boxShadow: uploading ? "none" : "0 2px 4px rgba(22, 163, 74, 0.2)",
                  transition: "all 0.2s ease",
                }}
              >
                {uploading ? (
                  <>
                    <CircularProgress size={16} color="inherit" />
                    <span>Uploading ({uploadProgress}%)...</span>
                  </>
                ) : (
                  <>
                    <CloudUploadIcon style={{ fontSize: 18 }} />
                    <span>Upload {previews.length} Photo{previews.length > 1 ? "s" : ""} Now</span>
                  </>
                )}
              </button>
            </div>

            {/* Upload Progress Bar & Status Text */}
            {uploading && (
              <div
                style={{
                  marginBottom: 14,
                  padding: "10px 14px",
                  backgroundColor: "#ffffff",
                  borderRadius: 8,
                  border: "1px solid #86efac",
                  boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 6,
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#15803d",
                  }}
                >
                  <span>{uploadStatus || "Uploading photos..."}</span>
                  <span style={{ fontWeight: 700 }}>{uploadProgress}%</span>
                </div>
                <div
                  style={{
                    width: "100%",
                    height: 8,
                    backgroundColor: "#e2e8f0",
                    borderRadius: 4,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${Math.max(5, uploadProgress)}%`,
                      height: "100%",
                      backgroundColor: uploadProgress === 100 ? "#16a34a" : "#2563eb",
                      backgroundImage: "linear-gradient(45deg, rgba(255, 255, 255, 0.15) 25%, transparent 25%, transparent 50%, rgba(255, 255, 255, 0.15) 50%, rgba(255, 255, 255, 0.15) 75%, transparent 75%, transparent)",
                      backgroundSize: "1rem 1rem",
                      transition: "width 0.2s ease",
                      animation: "uo-progress-stripes 1s linear infinite",
                    }}
                  />
                </div>
                <p style={{ margin: "6px 0 0", fontSize: 11, color: "#64748b" }}>
                  Please do not close this window while photos are being uploaded and saved.
                </p>
              </div>
            )}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))",
                gap: 10,
                maxHeight: 180,
                overflowY: "auto",
                padding: 4,
              }}
            >
              {previews.map((item, idx) => (
                <div
                  key={idx}
                  style={{
                    position: "relative",
                    borderRadius: 8,
                    overflow: "hidden",
                    border: "1px solid #cbd5e1",
                    backgroundColor: "#fff",
                    aspectRatio: "1/1",
                  }}
                >
                  <img
                    src={item.url}
                    alt={item.name}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                  <button
                    type="button"
                    onClick={() => removeSelectedFile(idx)}
                    title="Remove from queue"
                    style={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      backgroundColor: "#ef4444",
                      color: "#fff",
                      border: "none",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: 0,
                    }}
                  >
                    <CloseIcon style={{ fontSize: 14 }} />
                  </button>
                  <div
                    style={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      backgroundColor: "rgba(0,0,0,0.6)",
                      color: "#fff",
                      fontSize: 9,
                      padding: "2px 4px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.size} MB
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Existing Gallery Photos */}
        <div>
          <h4
            style={{
              margin: "0 0 12px",
              fontSize: 15,
              fontWeight: 700,
              color: "#1e293b",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>Current Photos on Website ({currentCount})</span>
            {currentCount > 0 && (
              <span style={{ fontSize: 12, fontWeight: 500, color: "#64748b" }}>
                Hover over a photo and click the red trash icon to delete it.
              </span>
            )}
          </h4>

          {loading ? (
            <div style={{ padding: "40px 0", textAlign: "center" }}>
              <CircularProgress size={32} />
              <p style={{ marginTop: 10, color: "#64748b", fontSize: 13 }}>
                Loading gallery photos...
              </p>
            </div>
          ) : currentCount === 0 ? (
            <div
              style={{
                padding: "36px 20px",
                textAlign: "center",
                backgroundColor: "#f8fafc",
                borderRadius: 12,
                border: "1px dashed #cbd5e1",
              }}
            >
              <CollectionsIcon style={{ fontSize: 36, color: "#94a3b8", marginBottom: 6 }} />
              <p style={{ margin: 0, fontWeight: 600, color: "#475569", fontSize: 14 }}>
                No photos in this facility gallery yet.
              </p>
              <p style={{ margin: "4px 0 0", color: "#94a3b8", fontSize: 12 }}>
                Use the upload box above to add up to 20 photos.
              </p>
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))",
                gap: 12,
              }}
            >
              {gallery.map((photo, idx) => (
                <div
                  key={photo.id || photo._id || idx}
                  style={{
                    position: "relative",
                    borderRadius: 10,
                    overflow: "hidden",
                    border: "1px solid #e2e8f0",
                    backgroundColor: "#f1f5f9",
                    aspectRatio: "4/3",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                  }}
                  className="gallery-item-card"
                >
                  <img
                    src={photo.url}
                    alt={photo.originalName || `Photo ${idx + 1}`}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    loading="lazy"
                  />

                  {/* Top Bar with Delete Button */}
                  <div
                    style={{
                      position: "absolute",
                      top: 6,
                      right: 6,
                      display: "flex",
                      gap: 4,
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => handleDeletePhoto(photo)}
                      title="Delete Photo"
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 6,
                        backgroundColor: "#ef4444",
                        color: "#fff",
                        border: "none",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                      }}
                    >
                      <DeleteOutlineIcon style={{ fontSize: 17 }} />
                    </button>
                  </div>

                  {/* Bottom Filename Overlay */}
                  <div
                    style={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      backgroundColor: "rgba(15, 23, 42, 0.75)",
                      color: "#fff",
                      fontSize: 10,
                      padding: "4px 6px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    #{idx + 1} {photo.originalName || "photo.jpg"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal Footer */}
      <div
        style={{
          padding: "14px 24px",
          backgroundColor: "#f8fafc",
          borderTop: "1px solid #e2e8f0",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
        }}
      >
        <button
          type="button"
          onClick={() => !uploading && onClose()}
          style={{
            padding: "8px 22px",
            backgroundColor: "#2563eb",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            cursor: "pointer",
            fontWeight: 600,
            fontSize: 13,
          }}
        >
          Done
        </button>
      </div>
      <style>
        {`
          @keyframes uo-progress-stripes {
            from { background-position: 1rem 0; }
            to { background-position: 0 0; }
          }
        `}
      </style>
    </Modal>
  );
}
